<script lang="ts">
interface Props {
    events?: FormTourEvent[]
    tourId?: uuid
    tourShipperCompanyId?: uuid | null
    tourCompanyId?: uuid
    isHubHaulier?: boolean
}
</script>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Draggable from 'vuedraggable'
import { v4 as uuid4 } from 'uuid'

import { uuid } from '@/types/general'
import { addressString } from '@/utils/string'
import { datetime } from '@/utils/dates'
import {
    EventType,
    FormTourEvent,
    MinimalShipment,
    ShipmentType,
} from '@/types/delivery-management'
import { shipmentTypes } from '@/utils/delivery-management'

import ShipmentModal from '@/components/delivery-management/ShipmentModal.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import ShipmentPicker from '@/components/delivery-management/manage-tour/ShipmentPicker.vue'

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'eventsChanged', value: FormTourEvent[]): void
    (e: 'shipmentSelected', value: MinimalShipment): void
    (e: 'update:open', value: boolean): void
}>()

const { t } = useI18n()

const events = ref(props.events || [])
const selectedShipmentId = ref<string>()
const shipmentModalOpen = ref(false)

const tourEventTypes = computed<Record<EventType, string>>(() => {
    return {
        [EventType.Shipment]: t('shipment'),
        [EventType.Bridge]: t('bridge'),
        [EventType.Ferry]: t('ferry'),
        [EventType.Waypoint]: t('waypoint'),
        [EventType.Decoupling]: t('decoupling'),
        [EventType.ShipmentLoad]: t('shipmentLoad'),
        [EventType.Coupling]: t('coupling'),
    }
})

const pickedShipmentIds = computed(() => {
    return events.value.map((event) => event.shipment?.id ?? '')
})

function tourEventType(tourEvent: FormTourEvent) {
    if (tourEvent.type !== EventType.Shipment) return tourEventTypes.value[tourEvent.type]

    return t(shipmentTypes[tourEvent.shipment!.type])
}

async function selectShipment(shipment: MinimalShipment) {
    events.value.push({
        id: uuid4(),
        shipment: shipment,
        waypoint: null,
        type: EventType.Shipment,
        order: events.value.length,
    })
    emit('eventsChanged', [...events.value])
}

async function removeTourEvent(event: FormTourEvent) {
    events.value = events.value.filter((e) => e.id !== event.id)
    emit('eventsChanged', [...events.value])
}

function saveOrder() {
    events.value = events.value.map((event, index) => ({ ...event, order: index }))
    emit('eventsChanged', [...events.value])
}

function showShipment(shipmentId: string) {
    selectedShipmentId.value = shipmentId
    shipmentModalOpen.value = true
}

function getShipmentDestinationName(shipment: MinimalShipment): string {
    if (shipment.type === ShipmentType.Pickup) {
        return shipment.sendingCompany.name
    }

    return shipment.receivingCompany.name
}

function getShipmentAddress(shipment: MinimalShipment): string {
    if (shipment.address) return shipment.address

    if (shipment.type === ShipmentType.Pickup) {
        return addressString(shipment.sendingLocation)
    }

    return addressString(shipment.receivingCompany)
}

watch(
    () => props.events,
    (value) => {
        events.value = value || []
    },
)
</script>

<template>
    <div class="flex w-full flex-row">
        <div class="flex w-full flex-col mr-6">
            <span class="dark:text-primary-50 mb-4">{{ t('currentEvents') }} </span>
            <Draggable
                v-model="events"
                ghost-class="drag-ghost-card"
                item-key="id"
                handle=".handle"
                @end="saveOrder"
            >
                <template #item="{ index }">
                    <div class="my-4 first:mt-0 flex flex-col justify-start space-y-3">
                        <div class="flex">
                            <span class="mr-3 text-xs">{{ index + 1 }} </span>

                            <div
                                class="flex w-full items-center justify-between space-x-3 rounded-xl bg-white p-3 shadow-md dark:bg-dark-400"
                            >
                                <div v-if="events[index].shipment" class="flex w-full space-x-3">
                                    <mdi:drag
                                        class="handle cursor-grab self-center text-xl text-dark-700 transition dark:text-white"
                                    />
                                    <div class="flex w-2/5 flex-shrink-0 flex-col">
                                        <span
                                            class="text-sm dark:text-primary-50 font-semibold"
                                            v-text="t('shipment')"
                                        />

                                        <div class="flex">
                                            <span
                                                class="text-sm dark:text-primary-50 mr-1 break-all"
                                                v-text="
                                                    events[index].shipment?.shipmentNumber ??
                                                    events[index].shipment?.name ??
                                                    '-'
                                                "
                                            />
                                            <mdi:eye
                                                class="ml-1 cursor-pointer hover:text-primary-400 dark:hover:text-primary-300 min-h-5 min-w-5"
                                                @click="showShipment(events[index].shipment!.id)"
                                            />
                                        </div>

                                        <span
                                            class="mr-2 text-sm dark:text-primary-50"
                                            v-text="tourEventType(events[index])"
                                        />
                                        <span
                                            class="text-xs"
                                            v-text="datetime(events[index].shipment!.plannedAt)"
                                        />
                                    </div>
                                    <div class="flex w-2/5 flex-col">
                                        <span
                                            class="text-sm dark:text-primary-50 font-semibold"
                                            v-text="
                                                events[index].shipment!.type === ShipmentType.Pickup
                                                    ? t('sender')
                                                    : t('receiver')
                                            "
                                        />

                                        <span
                                            v-truncate-tooltip="
                                                getShipmentDestinationName(events[index].shipment!)
                                            "
                                            class="text-sm dark:text-primary-50 truncate"
                                            v-text="
                                                getShipmentDestinationName(events[index].shipment!)
                                            "
                                        />

                                        <div
                                            class="text-xs dark:text-primary-200 break-words"
                                            v-text="getShipmentAddress(events[index].shipment!)"
                                        />
                                    </div>
                                </div>

                                <div v-if="events[index].waypoint" class="flex w-full space-x-3">
                                    <mdi:drag
                                        class="handle cursor-grab self-center text-xl text-dark-700 transition dark:text-white"
                                    />
                                    <div class="flex w-2/5 flex-shrink-0 flex-col">
                                        <span
                                            class="text-sm dark:text-primary-50 font-semibold"
                                            v-text="tourEventType(events[index])"
                                        />

                                        <div class="flex">
                                            <span
                                                class="text-sm dark:text-primary-50 mr-1 break-all"
                                                v-text="events[index].waypoint.name ?? '-'"
                                            />
                                        </div>

                                        <span
                                            class="text-xs"
                                            v-text="events[index].waypoint.description"
                                        />
                                    </div>
                                    <div class="flex w-2/5 space-y-1 flex-col">
                                        <div class="flex flex-col">
                                            <span
                                                class="text-sm dark:text-primary-50 font-semibold"
                                                v-text="t('arrival')"
                                            />

                                            <span
                                                class="text-xs dark:text-primary-50"
                                                v-text="events[index].waypoint.arrivalAddress"
                                            />
                                        </div>

                                        <div
                                            v-if="events[index].waypoint.departureAddress"
                                            class="flex flex-col"
                                        >
                                            <span
                                                class="text-sm dark:text-primary-50 font-semibold"
                                                v-text="t('departure')"
                                            />

                                            <span
                                                class="text-xs dark:text-primary-50"
                                                v-text="events[index].waypoint.departureAddress"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-shrink-0 items-center justify-end">
                                    <MyButton
                                        scheme="primary"
                                        type="button"
                                        size="small"
                                        @click="removeTourEvent(events[index])"
                                    >
                                        {{ t('remove') }}
                                    </MyButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </Draggable>
            <div v-if="events.length <= 0" class="m-4 flex justify-center">
                <span class="dark:text-primary-300" v-text="t('noCurrentEvents')" />
            </div>
        </div>

        <div class="flex w-full flex-col">
            <div class="flex items-center space-x-1 mb-4 dark:text-primary-50">
                {{ t('availableShipments') }}
            </div>

            <ShipmentPicker
                :tour-company-id="tourCompanyId"
                :tour-shipper-company-id="tourShipperCompanyId"
                :picked-shipment-ids="pickedShipmentIds"
                :filter="{ 'for-tour-or-without': props.tourId ?? 'none' }"
                @shipment-selected="selectShipment"
            />
        </div>

        <ShipmentModal
            v-if="selectedShipmentId"
            :id="selectedShipmentId"
            v-model:open="shipmentModalOpen"
            @update:open="selectedShipmentId = undefined"
        />
    </div>
</template>
